import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Plus, Pencil, Trash2, X, ChevronsUpDown, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import Gated from "@/components/Gated";
import MasterTable from "@/components/MasterTable";

// Regulator Type — first-level classification of the regulator entity
const TYPES = [
  "Government-Central",
  "Government-State",
  "Regulatory Authority",
  "Self-Regulatory Organization",
  "Bank",
  "Court",
  "Police",
  "Tax-GST-IT",
  "Custom",
];

const CUSTOM_KEY = "__custom__";
const EMPTY = {
  name: "",
  full_name: "",
  type: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  notes: "",
};

export default function RegulatorsSection() {
  const [items, setItems] = useState([]);
  const [master, setMaster] = useState([]);
  const [open, setOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/regulators"); setItems(data); }
    catch (_e) { /* silent */ }
  };
  const loadMaster = async () => {
    try { const { data } = await api.get("/regulators/master"); setMaster(data || []); }
    catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); loadMaster(); }, []);

  const masterNames = useMemo(() => master.map((m) => m.name), [master]);
  const resetForm = () => { setForm(EMPTY); setEditId(null); setCustomMode(false); };
  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ ...EMPTY, ...r });
    setCustomMode(!!r.name && !masterNames.includes(r.name));
    setOpen(true);
  };

  const pickName = (name) => {
    if (name === CUSTOM_KEY) {
      setCustomMode(true);
      setForm((f) => ({ ...f, name: "", full_name: "" }));
      setRegOpen(false);
      return;
    }
    const m = master.find((x) => x.name === name);
    setCustomMode(false);
    setForm((f) => ({
      ...f,
      name,
      // Always refresh full_name/type from master when a preset is picked
      full_name: m ? m.full_name : "",
      type: f.type || (m ? m.type : ""),
    }));
    setRegOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.type) { toast.error("Regulator Type is required"); return; }
    if (!form.name || !form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        full_name: (form.full_name || "").trim(),
        contact_name: (form.contact_name || "").trim(),
      };
      if (editId) await api.patch(`/regulators/${editId}`, payload);
      else await api.post("/regulators", payload);
      toast.success(editId ? "Regulator updated" : "Regulator added");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete regulator?")) return;
    await api.delete(`/regulators/${id}`);
    load();
    toast.success("Deleted");
  };

  return (
    <Gated feature="regulators">
      <section className="border border-border rounded-md bg-card p-6 card-elev" data-testid="regulators-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="overline flex items-center gap-2"><ShieldCheck size={12} /> Master data</div>
            <h2 className="font-display text-xl mt-1">Regulators</h2>
            <p className="text-sm text-muted-foreground mt-1">Government / bank / court / regulatory entities. Used to auto-flag inward parcels as priority.</p>
          </div>
          <Button data-testid="reg-new" onClick={openCreate} className="rounded-md bg-primary text-primary-foreground">
            <Plus size={13} className="mr-1" /> Add regulator
          </Button>
        </div>

        <MasterTable
          columns={[
            { key: "name", label: "Name", render: (r) => (
                <div>
                  <div className="font-semibold">{r.name}</div>
                  {r.full_name && (
                    <div className="text-[10px] text-muted-foreground leading-tight">{r.full_name}</div>
                  )}
                </div>
            )},
            { key: "type", label: "Type", className: "text-xs" },
            { key: "contact_name", label: "Contact", className: "text-xs", render: (r) => r.contact_name || "—" },
            { key: "contact_phone", label: "Phone / Email", className: "mono text-xs", render: (r) => (
                <div>
                  <div>{r.contact_phone || "—"}</div>
                  {r.contact_email && <div>{r.contact_email}</div>}
                </div>
            )},
            { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
                <div className="inline-flex items-center gap-3">
                  <button data-testid={`reg-edit-${r.id}`} onClick={() => openEdit(r)}
                          className="text-muted-foreground hover:text-primary" title="Edit"><Pencil size={13} /></button>
                  <button data-testid={`reg-del-${r.id}`} onClick={() => remove(r.id)}
                          className="text-destructive hover:text-primary" title="Delete"><Trash2 size={13} /></button>
                </div>
            )},
          ]}
          rows={items}
          searchKeys={["name", "full_name", "type", "contact_name", "contact_phone", "contact_email"]}
          getRowKey={(r) => r.id}
          testidPrefix="reg"
          defaultSort={{ key: "name", dir: "asc" }}
          emptyText="No regulators yet."
        />

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogContent className="rounded-md max-w-lg" data-testid="reg-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editId ? "Edit regulator" : "Add regulator"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              {/* 1 · Regulator Type (mandatory) */}
              <label className="block">
                <span className="overline">Regulator Type *</span>
                <select
                  data-testid="reg-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  required
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="" disabled>Select type…</option>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>

              {/* 2 · Name (mandatory) — searchable picker; renders short name big + full name small */}
              <label className="block">
                <span className="overline">Name *</span>
                {customMode ? (
                  <div className="mt-1 flex gap-1">
                    <Input
                      data-testid="reg-name-custom"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      placeholder="e.g. AMFI"
                      className="rounded-md"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setCustomMode(false); setForm({ ...form, name: "", full_name: "" }); }}
                      className="rounded-md px-2"
                      title="Back to dropdown"
                    >
                      <X size={13} />
                    </Button>
                  </div>
                ) : (
                  <Popover open={regOpen} onOpenChange={setRegOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={regOpen}
                        data-testid="reg-name-trigger"
                        className="mt-1 w-full min-h-[2.5rem] px-3 py-1.5 rounded-md border border-input bg-background text-sm flex items-center justify-between text-left"
                      >
                        {form.name ? (
                          <span className="flex flex-col leading-tight">
                            <span className="font-semibold">{form.name}</span>
                            {form.full_name && (
                              <span className="text-[10px] text-muted-foreground">{form.full_name}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Select name…</span>
                        )}
                        <ChevronsUpDown size={14} className="opacity-60 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[300px]" align="start" data-testid="reg-name-popover">
                      <Command>
                        <CommandInput placeholder="Search name…" data-testid="reg-name-search" />
                        <CommandList>
                          <CommandEmpty>No matches. Choose "Add other…" below.</CommandEmpty>
                          <CommandGroup heading="Predefined regulators">
                            {master.map((m) => (
                              <CommandItem
                                key={m.name}
                                value={`${m.name} ${m.full_name}`}
                                onSelect={() => pickName(m.name)}
                                data-testid={`reg-name-opt-${m.name}`}
                              >
                                <Check size={14} className={`mr-2 ${form.name === m.name ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex flex-col leading-tight">
                                  <span className="font-semibold">{m.name}</span>
                                  <span className="text-[10px] text-muted-foreground">{m.full_name}</span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          <CommandGroup>
                            <CommandItem
                              value="__custom__"
                              onSelect={() => pickName(CUSTOM_KEY)}
                              data-testid="reg-name-opt-custom"
                            >
                              <Plus size={14} className="mr-2" />
                              <span>Add other… (type manually)</span>
                            </CommandItem>
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </label>

              {/* 3 · Organization name (auto-filled from master, editable, optional) */}
              <label className="block">
                <span className="overline">Organization name</span>
                <Input
                  data-testid="reg-fullname"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="e.g. Association of Mutual Funds in India"
                  className="mt-1 rounded-md"
                />
              </label>

              {/* 4 · Contact Name (optional) */}
              <label className="block">
                <span className="overline">Contact Name</span>
                <Input
                  data-testid="reg-contact"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Person or department (optional)"
                  className="mt-1 rounded-md"
                />
              </label>

              {/* Phone / Email / Address — unchanged */}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Phone</span>
                  <Input data-testid="reg-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1 rounded-md font-mono" />
                </label>
                <label className="block">
                  <span className="overline">Email</span>
                  <Input data-testid="reg-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1 rounded-md" />
                </label>
              </div>

              <label className="block">
                <span className="overline">Address</span>
                <Input data-testid="reg-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-md" />
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-md">
                  <X size={13} className="mr-1" />Cancel
                </Button>
                <Button type="submit" data-testid="reg-submit" className="rounded-md bg-primary text-primary-foreground">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </section>
    </Gated>
  );
}
